import PropTypes from 'prop-types';
import React from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { userIdentityShape, childrenShape, catalogBibShape } from '@bibliocommons/bc-prop-types';
import { TextButton } from '@bibliocommons/base-buttons';
import { Heart, HeartOutline } from '@bibliocommons/base-icons';
import { RatingStars } from '@bibliocommons/rating';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import ShortFormattedDate from 'app/components/shared/ShortFormattedDate';
import { selectShelvesForCurrentUser } from 'app/selectors/ShelfSelector';
import PrivacyIndicator from 'app/components/bibs/shared/ugc/shared/PrivacyIndicator';
import ContentWarnings from 'app/components/bibs/shared/ugc/shared/ContentWarnings';

import UserCard from 'app/components/shared/UserCard';
import useFlags from '../shared/useFlags';
import FlagUgcButtonContainer from '../FlagUgcButton';
import UgcLikesButtonContainer from '../UgcLikesButton';
import OptionsDropdown from '../OptionsDropdown';

import './TextUgcDisplay.scss';

export default function TextUgcDisplay({
  ugcType,
  catalogBib,
  author,
  displayStaffBadge,
  compact,
  createdDate,
  rating,
  likesCount,
  isLikedByCurrentUser,
  id,
  children,
  className,
  optionsDropdownRef,
  handleDelete,
  handleEdit,
  handleLike,
  handleUnlike,
  hidePermalink
}) {
  const { flags, contentIsHidden, placeholder, toggleContent } = useFlags(id);
  const shelfItem = useSelector(selectShelvesForCurrentUser).get(catalogBib.get('metadataId'));

  function renderContentOrPlaceholder() {
    return contentIsHidden ? placeholder : children;
  }

  function renderHeader() {
    const controls = (
      <div className="right-side">
        <ContentWarnings
          author={author}
          flags={flags}
          contentIsHidden={contentIsHidden}
          toggleContent={toggleContent}
          ugcType={ugcType}
        />
        <PrivacyIndicator author={author} shelfItem={shelfItem} />
        <OptionsDropdown
          id={id}
          ugcType={ugcType}
          author={author}
          handleDelete={handleDelete}
          handleEdit={handleEdit}
          ref={optionsDropdownRef}
          hidePermalink={hidePermalink}
        />
      </div>
    );

    const screenReaderLabel = (
      <FormattedMessage
        id="ugc_display_rating_sr_label"
        values={{ username: author.get('name'), rating: rating / 20 }}
      />
    );
    const otherInfo = (
      <div>
        {!compact && <ShortFormattedDate value={createdDate} />}
        {!compact && rating && (
          <RatingStars className="ugc-display-rating" rating={rating} screenReaderLabel={screenReaderLabel} />
        )}
      </div>
    );

    return (
      <div className="ugc-display__header">
        <div className="user-area">
          <UserCard hasAvatar={!compact} hasUserBadge={displayStaffBadge} ugcType={ugcType} user={author} />
        </div>
        {controls}
        {otherInfo}
      </div>
    );
  }

  function renderFooter() {
    return (
      <div className="ugc-display__footer">
        {!!likesCount && <UgcLikesButtonContainer contentId={id} count={likesCount} />}
        {ugcType === 'comment' &&
          (isLikedByCurrentUser ? (
            <TextButton
              className="ugc-display__unlike-button"
              dataKey="ugc-display-unlike"
              size="small"
              renderLeadingIcon={() => <Heart />}
              handleClick={handleUnlike}
            >
              <FormattedMessage id="you_liked_this" />
            </TextButton>
          ) : (
            <TextButton
              className="ugc-display__like-button"
              dataKey="ugc-display-like"
              size="small"
              renderLeadingIcon={() => <HeartOutline />}
              handleClick={handleLike}
            >
              <span aria-hidden>
                <FormattedMessage id="like" />
              </span>
              <ScreenReaderMessage>
                <FormattedMessage id="sr_like" values={{ ugcType }} />
              </ScreenReaderMessage>
            </TextButton>
          ))}

        {flags.get('flaggableTypes')?.size > 0 && <FlagUgcButtonContainer id={id} ugcType={ugcType} />}
      </div>
    );
  }

  return (
    <div className={cn('cp-text-ugc-display', className)}>
      {renderHeader()}
      {renderContentOrPlaceholder()}
      {renderFooter()}
    </div>
  );
}

TextUgcDisplay.defaultProps = {
  displayStaffBadge: true,
  hidePermalink: false
};

TextUgcDisplay.propTypes = {
  ugcType: PropTypes.string,
  catalogBib: catalogBibShape.isRequired,
  author: userIdentityShape,
  displayStaffBadge: PropTypes.bool,
  compact: PropTypes.bool,
  createdDate: PropTypes.string,
  rating: PropTypes.number,
  likesCount: PropTypes.number,
  isLikedByCurrentUser: PropTypes.bool,
  id: PropTypes.number,
  children: childrenShape,
  optionsDropdownRef: PropTypes.object,
  className: PropTypes.string,
  handleDelete: PropTypes.func,
  handleEdit: PropTypes.func,
  handleLike: PropTypes.func,
  handleUnlike: PropTypes.func,
  hidePermalink: PropTypes.bool
};
